import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Shield, 
  Save, 
  RefreshCw,
  Loader2,
  Check,
  X,
  Eye,
  AlertTriangle,
  Lock,
  Heart,
  MessageCircle,
  Share2,
  Upload,
  Swords,
  Star,
  Zap,
  Tag,
  Users,
  Crown,
  Info
} from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

// Default permission matrix
const DEFAULT_PERMISSIONS = {
  // Roles
  roles: ['listener', 'artist', 'podcaster', 'streamer', 'organization', 'studio', 'admin', 'owner'],
  
  // Tiers (for artist roles)
  tiers: ['starter', 'growth', 'pro', 'elite', 'executive', 'label'],
  
  // Permission definitions
  permissions: {
    comment: { 
      label: 'Comment', 
      icon: 'MessageCircle', 
      description: 'Comment on posts and reels',
      category: 'engagement'
    },
    like: { 
      label: 'Like', 
      icon: 'Heart', 
      description: 'Like content',
      category: 'engagement'
    },
    share: { 
      label: 'Share', 
      icon: 'Share2', 
      description: 'Share content',
      category: 'engagement'
    },
    tag: { 
      label: 'Tag Users', 
      icon: 'Tag', 
      description: 'Tag other users in posts',
      category: 'engagement'
    },
    upload_audio: { 
      label: 'Upload Audio', 
      icon: 'Upload', 
      description: 'Upload music tracks',
      category: 'content'
    },
    upload_video: { 
      label: 'Upload Video', 
      icon: 'Upload', 
      description: 'Upload music videos',
      category: 'content'
    },
    upload_reel: { 
      label: 'Upload Clip', 
      icon: 'Upload', 
      description: 'Upload Soundstage Clips',
      category: 'content'
    },
    create_post: { 
      label: 'Create Post', 
      icon: 'Upload', 
      description: 'Create posts',
      category: 'content'
    },
    battle: { 
      label: 'Battle', 
      icon: 'Swords', 
      description: 'Participate in cypher battles',
      category: 'features'
    },
    feature: { 
      label: 'Feature', 
      icon: 'Star', 
      description: 'Be featured on homepage',
      category: 'features'
    },
    boost: { 
      label: 'Boost', 
      icon: 'Zap', 
      description: 'Boost posts for visibility',
      category: 'features'
    },
    go_live: { 
      label: 'Go Live', 
      icon: 'Users', 
      description: 'Start live streams',
      category: 'features'
    },
  },
  
  // Default matrix: role/tier -> permission -> { allowed, rateLimited, limit }
  matrix: {
    // Listeners - full engagement, limited content
    'listener': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true, requiresMutualFollow: true },
      upload_audio: { allowed: false },
      upload_video: { allowed: false },
      upload_reel: { allowed: false },
      create_post: { allowed: false },
      battle: { allowed: false },
      feature: { allowed: false },
      boost: { allowed: false },
      go_live: { allowed: false },
    },
    
    // Artist - Starter (Free)
    'artist_starter': {
      comment: { allowed: true, rateLimited: true, limit: 5 },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true, requiresMutualFollow: true },
      upload_audio: { allowed: true, limit: 3 },
      upload_video: { allowed: false },
      upload_reel: { allowed: true, limit: 2 },
      create_post: { allowed: true },
      battle: { allowed: false },
      feature: { allowed: false },
      boost: { allowed: false },
      go_live: { allowed: false },
    },
    
    // Artist - Growth
    'artist_growth': {
      comment: { allowed: true, rateLimited: true, limit: 10 },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true, requiresMutualFollow: true },
      upload_audio: { allowed: true, limit: 10 },
      upload_video: { allowed: true, limit: 2 },
      upload_reel: { allowed: true, limit: 5 },
      create_post: { allowed: true },
      battle: { allowed: true },
      feature: { allowed: false },
      boost: { allowed: true, limit: 3 },
      go_live: { allowed: false },
    },
    
    // Artist - Pro
    'artist_pro': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true },
      upload_audio: { allowed: true, limit: 50 },
      upload_video: { allowed: true, limit: 10 },
      upload_reel: { allowed: true },
      create_post: { allowed: true },
      battle: { allowed: true },
      feature: { allowed: true },
      boost: { allowed: true, limit: 10 },
      go_live: { allowed: true },
    },
    
    // Artist - Elite (Pro+)
    'artist_elite': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true },
      upload_audio: { allowed: true },
      upload_video: { allowed: true },
      upload_reel: { allowed: true },
      create_post: { allowed: true },
      battle: { allowed: true },
      feature: { allowed: true },
      boost: { allowed: true },
      go_live: { allowed: true },
    },
    
    // Artist - Executive
    'artist_executive': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true },
      upload_audio: { allowed: true },
      upload_video: { allowed: true },
      upload_reel: { allowed: true },
      create_post: { allowed: true },
      battle: { allowed: true },
      feature: { allowed: true },
      boost: { allowed: true },
      go_live: { allowed: true },
    },
    
    // Artist - Label
    'artist_label': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true },
      upload_audio: { allowed: true },
      upload_video: { allowed: true },
      upload_reel: { allowed: true },
      create_post: { allowed: true },
      battle: { allowed: true },
      feature: { allowed: true },
      boost: { allowed: true },
      go_live: { allowed: true },
    },
    
    // Organization
    'organization': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true, requiresMutualFollow: true },
      upload_audio: { allowed: false },
      upload_video: { allowed: true },
      upload_reel: { allowed: true },
      create_post: { allowed: true },
      battle: { allowed: false },
      feature: { allowed: true },
      boost: { allowed: true },
      go_live: { allowed: true },
    },
    
    // Admin
    'admin': {
      comment: { allowed: true },
      like: { allowed: true },
      share: { allowed: true },
      tag: { allowed: true },
      upload_audio: { allowed: true },
      upload_video: { allowed: true },
      upload_reel: { allowed: true },
      create_post: { allowed: true },
      battle: { allowed: true },
      feature: { allowed: true },
      boost: { allowed: true },
      go_live: { allowed: true },
    },
    
    // Owner - Always full access (protected)
    'owner': {
      comment: { allowed: true, protected: true },
      like: { allowed: true, protected: true },
      share: { allowed: true, protected: true },
      tag: { allowed: true, protected: true },
      upload_audio: { allowed: true, protected: true },
      upload_video: { allowed: true, protected: true },
      upload_reel: { allowed: true, protected: true },
      create_post: { allowed: true, protected: true },
      battle: { allowed: true, protected: true },
      feature: { allowed: true, protected: true },
      boost: { allowed: true, protected: true },
      go_live: { allowed: true, protected: true },
    },
  }
};

// Icon component map
const IconMap = {
  MessageCircle,
  Heart,
  Share2,
  Tag,
  Upload,
  Swords,
  Star,
  Zap,
  Users,
};

export default function AdminPermissionMatrixPage() {
  const { user, isOwner, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionMatrix, setPermissionMatrix] = useState(DEFAULT_PERMISSIONS);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState('listener');
  const [activeTab, setActiveTab] = useState('matrix');
  
  // Test as User simulation state
  const [simulateRole, setSimulateRole] = useState('listener');
  const [simulateTier, setSimulateTier] = useState('starter');
  const [simulatedPermissions, setSimulatedPermissions] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    if (!isOwner && user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    loadPermissions();
  }, [isOwner, user, navigate]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/permissions-matrix`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.matrix) {
          setPermissionMatrix(prev => ({
            ...prev,
            matrix: { ...prev.matrix, ...data.matrix }
          }));
        }
      } else {
        // Use defaults if no saved matrix
        console.log('[PermissionMatrix] Using default permissions');
      }
    } catch (error) {
      console.error('[PermissionMatrix] Error loading:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/permissions-matrix`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ matrix: permissionMatrix.matrix })
      });

      if (response.ok) {
        toast.success('Permissions saved successfully');
        setHasChanges(false);
        // Refresh simulation if active
        if (simulatedPermissions) {
          simulatePermissions();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save permissions');
      }
    } catch (error) {
      console.error('[PermissionMatrix] Save error:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const simulatePermissions = async () => {
    setSimulating(true);
    try {
      const response = await fetch(
        `${API}/admin/permissions-matrix/simulate?role=${simulateRole}&tier=${simulateTier}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSimulatedPermissions(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to simulate permissions');
      }
    } catch (error) {
      console.error('[PermissionMatrix] Simulation error:', error);
      toast.error('Failed to simulate permissions');
    } finally {
      setSimulating(false);
    }
  };

  const togglePermission = (roleKey, permKey) => {
    const currentPerm = permissionMatrix.matrix[roleKey]?.[permKey];
    
    // Prevent disabling owner permissions (safety)
    if (roleKey === 'owner' || currentPerm?.protected) {
      toast.error('Owner permissions cannot be modified');
      return;
    }

    setPermissionMatrix(prev => ({
      ...prev,
      matrix: {
        ...prev.matrix,
        [roleKey]: {
          ...prev.matrix[roleKey],
          [permKey]: {
            ...currentPerm,
            allowed: !currentPerm?.allowed
          }
        }
      }
    }));
    setHasChanges(true);
  };

  const getPermissionCell = (roleKey, permKey) => {
    const perm = permissionMatrix.matrix[roleKey]?.[permKey];
    if (!perm) return null;

    const isProtected = perm.protected || roleKey === 'owner';
    
    return (
      <div className="flex flex-col items-center gap-1">
        <Switch
          checked={perm.allowed}
          onCheckedChange={() => togglePermission(roleKey, permKey)}
          disabled={isProtected}
          className={isProtected ? 'opacity-50' : ''}
        />
        {perm.rateLimited && perm.allowed && (
          <Badge variant="outline" className="text-[10px] px-1">
            {perm.limit}/hr
          </Badge>
        )}
        {perm.requiresMutualFollow && perm.allowed && (
          <Badge variant="outline" className="text-[10px] px-1 text-amber-500">
            mutual
          </Badge>
        )}
        {perm.limit && !perm.rateLimited && perm.allowed && (
          <Badge variant="outline" className="text-[10px] px-1 text-blue-500">
            {perm.limit}
          </Badge>
        )}
        {isProtected && (
          <Lock className="w-3 h-3 text-muted-foreground" />
        )}
      </div>
    );
  };

  const getRoleDisplayName = (roleKey) => {
    const names = {
      'listener': 'Listener',
      'artist_starter': 'Artist (Free)',
      'artist_growth': 'Artist (Growth)',
      'artist_pro': 'Artist (Pro)',
      'artist_elite': 'Artist (Pro+)',
      'artist_executive': 'Artist (Executive)',
      'artist_label': 'Artist (Label)',
      'organization': 'Organization',
      'admin': 'Admin',
      'owner': 'Owner',
    };
    return names[roleKey] || roleKey;
  };

  const getPreviewButtons = (roleKey) => {
    const perms = permissionMatrix.matrix[roleKey] || {};
    const buttons = [];

    Object.entries(perms).forEach(([key, perm]) => {
      if (!perm.allowed) return;
      
      const permDef = permissionMatrix.permissions[key];
      if (!permDef) return;

      const Icon = IconMap[permDef.icon] || Shield;
      buttons.push({
        key,
        label: permDef.label,
        icon: Icon,
        rateLimited: perm.rateLimited,
        limit: perm.limit,
        requiresMutualFollow: perm.requiresMutualFollow,
      });
    });

    return buttons;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const roleKeys = [
    'listener',
    'artist_starter',
    'artist_growth',
    'artist_pro',
    'artist_elite',
    'artist_executive',
    'artist_label',
    'organization',
    'admin',
    'owner',
  ];

  const permKeys = Object.keys(permissionMatrix.permissions);

  return (
    <div className="min-h-screen bg-background p-6" data-testid="admin-permission-matrix-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              Permission Matrix
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure what each role + tier can do across the platform
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={loadPermissions}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Reload
            </Button>
            <Button
              onClick={savePermissions}
              disabled={saving || !hasChanges}
              className="bg-primary"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Unsaved changes warning */}
        {hasChanges && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <p className="text-amber-500">You have unsaved changes</p>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="matrix">
              <Shield className="w-4 h-4 mr-2" />
              Permission Matrix
            </TabsTrigger>
            <TabsTrigger value="preview">
              <Eye className="w-4 h-4 mr-2" />
              Live Preview
            </TabsTrigger>
            <TabsTrigger value="simulate" data-testid="simulate-tab">
              <Users className="w-4 h-4 mr-2" />
              Test as User
            </TabsTrigger>
          </TabsList>

          {/* Matrix Tab */}
          <TabsContent value="matrix">
            <Card className="glass-card border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 font-heading uppercase text-sm sticky left-0 bg-background z-10">
                        Role / Tier
                      </th>
                      {permKeys.map(key => {
                        const perm = permissionMatrix.permissions[key];
                        const Icon = IconMap[perm.icon] || Shield;
                        return (
                          <th key={key} className="p-3 text-center min-w-[100px]">
                            <div className="flex flex-col items-center gap-1">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                              <span className="text-xs font-medium">{perm.label}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {roleKeys.map((roleKey, i) => (
                      <tr 
                        key={roleKey}
                        className={`border-b border-border ${
                          roleKey === 'owner' ? 'bg-amber-500/5' : 
                          roleKey === 'admin' ? 'bg-blue-500/5' : ''
                        }`}
                      >
                        <td className="p-4 sticky left-0 bg-background z-10">
                          <div className="flex items-center gap-2">
                            {roleKey === 'owner' && <Crown className="w-4 h-4 text-amber-500" />}
                            {roleKey === 'admin' && <Shield className="w-4 h-4 text-blue-500" />}
                            <span className="font-medium text-sm">
                              {getRoleDisplayName(roleKey)}
                            </span>
                          </div>
                        </td>
                        {permKeys.map(permKey => (
                          <td key={permKey} className="p-3 text-center">
                            {getPermissionCell(roleKey, permKey)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-8 h-5 rounded bg-primary/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <span>Allowed</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">5/hr</Badge>
                <span>Rate limited</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] text-amber-500">mutual</Badge>
                <span>Requires mutual follow</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] text-blue-500">10</Badge>
                <span>Upload limit</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-3 h-3" />
                <span>Protected (cannot disable)</span>
              </div>
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Role Selector */}
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading text-lg font-bold uppercase mb-4">
                  Select Role to Preview
                </h3>
                <div className="space-y-2">
                  {roleKeys.map(roleKey => (
                    <button
                      key={roleKey}
                      onClick={() => setSelectedPreview(roleKey)}
                      className={`w-full p-3 rounded-lg text-left transition-all flex items-center justify-between ${
                        selectedPreview === roleKey
                          ? 'bg-primary/20 border border-primary/50'
                          : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {roleKey === 'owner' && <Crown className="w-4 h-4 text-amber-500" />}
                        {roleKey === 'admin' && <Shield className="w-4 h-4 text-blue-500" />}
                        <span className="font-medium">{getRoleDisplayName(roleKey)}</span>
                      </div>
                      {selectedPreview === roleKey && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </Card>

              {/* Preview Panel */}
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading text-lg font-bold uppercase mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  UI Preview: {getRoleDisplayName(selectedPreview)}
                </h3>
                
                <div className="space-y-6">
                  {/* Engagement Actions */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Engagement Actions</p>
                    <div className="flex flex-wrap gap-2">
                      {getPreviewButtons(selectedPreview)
                        .filter(b => ['comment', 'like', 'share', 'tag'].includes(b.key))
                        .map(btn => (
                          <Button
                            key={btn.key}
                            variant="outline"
                            size="sm"
                            className="relative"
                          >
                            <btn.icon className="w-4 h-4 mr-2" />
                            {btn.label}
                            {btn.rateLimited && (
                              <Badge variant="secondary" className="ml-2 text-[10px]">
                                {btn.limit}/hr
                              </Badge>
                            )}
                            {btn.requiresMutualFollow && (
                              <Badge variant="secondary" className="ml-2 text-[10px] bg-amber-500/20 text-amber-500">
                                mutual
                              </Badge>
                            )}
                          </Button>
                        ))}
                    </div>
                    
                    {/* Disabled engagement actions */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['comment', 'like', 'share', 'tag']
                        .filter(key => !permissionMatrix.matrix[selectedPreview]?.[key]?.allowed)
                        .map(key => {
                          const perm = permissionMatrix.permissions[key];
                          const Icon = IconMap[perm.icon] || Shield;
                          return (
                            <Button
                              key={key}
                              variant="outline"
                              size="sm"
                              disabled
                              className="opacity-40"
                            >
                              <Icon className="w-4 h-4 mr-2" />
                              {perm.label}
                              <X className="w-3 h-3 ml-2 text-red-500" />
                            </Button>
                          );
                        })}
                    </div>
                  </div>

                  {/* Content Actions */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Content Actions</p>
                    <div className="flex flex-wrap gap-2">
                      {getPreviewButtons(selectedPreview)
                        .filter(b => ['upload_audio', 'upload_video', 'upload_reel', 'create_post'].includes(b.key))
                        .map(btn => (
                          <Button
                            key={btn.key}
                            variant="outline"
                            size="sm"
                          >
                            <btn.icon className="w-4 h-4 mr-2" />
                            {btn.label}
                            {btn.limit && !btn.rateLimited && (
                              <Badge variant="secondary" className="ml-2 text-[10px]">
                                max {btn.limit}
                              </Badge>
                            )}
                          </Button>
                        ))}
                    </div>
                    
                    {/* Disabled content actions */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['upload_audio', 'upload_video', 'upload_reel', 'create_post']
                        .filter(key => !permissionMatrix.matrix[selectedPreview]?.[key]?.allowed)
                        .map(key => {
                          const perm = permissionMatrix.permissions[key];
                          const Icon = IconMap[perm.icon] || Shield;
                          return (
                            <Button
                              key={key}
                              variant="outline"
                              size="sm"
                              disabled
                              className="opacity-40"
                            >
                              <Icon className="w-4 h-4 mr-2" />
                              {perm.label}
                              <X className="w-3 h-3 ml-2 text-red-500" />
                            </Button>
                          );
                        })}
                    </div>
                  </div>

                  {/* Feature Actions */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Feature Actions</p>
                    <div className="flex flex-wrap gap-2">
                      {getPreviewButtons(selectedPreview)
                        .filter(b => ['battle', 'feature', 'boost', 'go_live'].includes(b.key))
                        .map(btn => (
                          <Button
                            key={btn.key}
                            variant="outline"
                            size="sm"
                          >
                            <btn.icon className="w-4 h-4 mr-2" />
                            {btn.label}
                            {btn.limit && !btn.rateLimited && (
                              <Badge variant="secondary" className="ml-2 text-[10px]">
                                max {btn.limit}
                              </Badge>
                            )}
                          </Button>
                        ))}
                    </div>
                    
                    {/* Disabled feature actions */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['battle', 'feature', 'boost', 'go_live']
                        .filter(key => !permissionMatrix.matrix[selectedPreview]?.[key]?.allowed)
                        .map(key => {
                          const perm = permissionMatrix.permissions[key];
                          const Icon = IconMap[perm.icon] || Shield;
                          return (
                            <Button
                              key={key}
                              variant="outline"
                              size="sm"
                              disabled
                              className="opacity-40"
                            >
                              <Icon className="w-4 h-4 mr-2" />
                              {perm.label}
                              <X className="w-3 h-3 ml-2 text-red-500" />
                            </Button>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg flex items-start gap-3">
                  <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    This preview shows what UI buttons would appear for a user with the selected role/tier.
                    Grayed out buttons indicate disabled permissions.
                  </p>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Test as User Tab */}
          <TabsContent value="simulate">
            <Card className="glass-card border-border p-6">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/20">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Test as User</h3>
                    <p className="text-sm text-muted-foreground">
                      Simulate how permissions appear for different role/tier combinations
                    </p>
                  </div>
                </div>

                {/* Role/Tier Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/20 rounded-lg">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Role</label>
                    <select
                      value={simulateRole}
                      onChange={(e) => setSimulateRole(e.target.value)}
                      className="w-full p-2 rounded-md bg-background border border-border text-sm"
                      data-testid="simulate-role-select"
                    >
                      <option value="listener">Listener</option>
                      <option value="artist">Artist</option>
                      <option value="podcaster">Podcaster</option>
                      <option value="streamer">Streamer</option>
                      <option value="organization">Organization</option>
                      <option value="studio">Studio</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tier (for creators)</label>
                    <select
                      value={simulateTier}
                      onChange={(e) => setSimulateTier(e.target.value)}
                      className="w-full p-2 rounded-md bg-background border border-border text-sm"
                      disabled={simulateRole === 'listener' || simulateRole === 'organization' || simulateRole === 'admin' || simulateRole === 'owner'}
                      data-testid="simulate-tier-select"
                    >
                      <option value="starter">Free (Starter)</option>
                      <option value="growth">Growth</option>
                      <option value="pro">Pro</option>
                      <option value="elite">Pro+ (Elite)</option>
                      <option value="executive">Executive</option>
                      <option value="label">Label / Enterprise</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <Button
                      onClick={simulatePermissions}
                      disabled={simulating}
                      className="w-full"
                      data-testid="simulate-btn"
                    >
                      {simulating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Simulate
                    </Button>
                  </div>
                </div>

                {/* Results */}
                {simulatedPermissions && (
                  <div className="space-y-4" data-testid="simulation-results">
                    {/* User Info */}
                    <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Crown className="w-5 h-5 text-primary" />
                          <span className="font-medium">Simulated User</span>
                        </div>
                        <Badge variant={simulatedPermissions.matrix_loaded ? "default" : "outline"}>
                          {simulatedPermissions.matrix_loaded ? "Custom Matrix Active" : "Default Rules"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Role:</span>
                          <span className="ml-2 font-medium capitalize">{simulatedPermissions.role}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Tier:</span>
                          <span className="ml-2 font-medium">{simulatedPermissions.tier_name}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Admin:</span>
                          <span className={`ml-2 font-medium ${simulatedPermissions.is_admin ? 'text-green-500' : 'text-muted-foreground'}`}>
                            {simulatedPermissions.is_admin ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Full Tier:</span>
                          <span className={`ml-2 font-medium ${simulatedPermissions.is_full_tier ? 'text-green-500' : 'text-muted-foreground'}`}>
                            {simulatedPermissions.is_full_tier ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        {simulatedPermissions.summary}
                      </div>
                    </div>

                    {/* Permission Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Engagement Permissions */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Engagement</h4>
                        <div className="space-y-1">
                          {['like', 'share', 'comment', 'tag', 'reply'].map(key => {
                            const perm = simulatedPermissions[key];
                            if (!perm) return null;
                            return (
                              <div key={key} className={`flex items-center justify-between p-2 rounded-lg ${perm.allowed ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                <span className="capitalize text-sm">{key}</span>
                                <div className="flex items-center gap-2">
                                  {perm.rate_limited && (
                                    <Badge variant="outline" className="text-xs">
                                      {perm.limit_per_hour}/hr
                                    </Badge>
                                  )}
                                  {perm.requires_mutual_follow && (
                                    <Badge variant="outline" className="text-xs text-amber-500">
                                      mutual
                                    </Badge>
                                  )}
                                  {perm.allowed ? (
                                    <Check className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <X className="w-4 h-4 text-red-500" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Content Permissions */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Content</h4>
                        <div className="space-y-1">
                          {['upload_audio', 'upload_video', 'upload_reel', 'create_post'].map(key => {
                            const perm = simulatedPermissions[key];
                            if (!perm) return null;
                            return (
                              <div key={key} className={`flex items-center justify-between p-2 rounded-lg ${perm.allowed ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                <span className="text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                {perm.allowed ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <X className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Feature Permissions */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Features</h4>
                        <div className="space-y-1">
                          {['go_live', 'battle'].map(key => {
                            const perm = simulatedPermissions[key];
                            if (!perm) return null;
                            return (
                              <div key={key} className={`flex items-center justify-between p-2 rounded-lg ${perm.allowed ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                <span className="text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                {perm.allowed ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <X className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Tip */}
                    <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg text-sm">
                      <Info className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <p className="text-muted-foreground">
                        This shows what permissions a <strong>{simulateRole}</strong> user 
                        {simulateRole === 'artist' && <span> with <strong>{simulateTier}</strong> tier</span>} would have 
                        based on your current saved matrix. Make changes in the Permission Matrix tab and save to update these results.
                      </p>
                    </div>
                  </div>
                )}

                {/* No simulation yet */}
                {!simulatedPermissions && !simulating && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Select a role and tier, then click Simulate</p>
                    <p className="text-sm">to see how permissions would appear for that user</p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
